import React, { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import useModal from '@tenant/utility/hooks/useModal';
import {
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  InputAdornment,
  TextField,
} from '@mui/material';
import useBindDomainManual from 'redux/slices/containerApp/hooks/useBindDomainManual';
import { LoadingButton } from '@mui/lab';
import useCheckDomain from 'redux/slices/containerApp/hooks/useCheckDomain';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import { useQueryClient } from '@tanstack/react-query';

const regexDomain = /^[a-z][a-z0-9-]*$/;

const BindDomainManualAction = ({
  containerId,
  appInstanceId,
  open,
  onCloseModal,
}) => {
  const queryClient = useQueryClient();
  const action = useBindDomainManual();
  const checkDomainAction = useCheckDomain();
  const { visible, onShow, onClose } = useModal();
  const [customDomain, setCustomDomain] = useState('');
  const [isChecking, setIsChecking] = useState(false);
  const show = !!(open ?? visible);

  const prefix =
    process.env.REACT_APP_JL_ENV && process.env.REACT_APP_JL_ENV !== 'Live'
      ? process.env.REACT_APP_JL_ENV.toLowerCase()
      : '';

  const unValidCustomDomain = useMemo(() => {
    if (!customDomain) {
      return '';
    }

    if ((customDomain + prefix).length > 62) {
      return 'Name must be at most 62 characters';
    }

    if (!regexDomain.test(customDomain)) {
      return 'Name is invalid';
    }

    if (isChecking && !checkDomainAction.data) {
      return 'Sorry, that name has already been taken.';
    }
  }, [customDomain, prefix, checkDomainAction.data, isChecking]);

  const disabledSubmit = useMemo(
    () => !customDomain || !checkDomainAction.data || !isChecking,
    [customDomain, checkDomainAction.data, isChecking],
  );

  const handleClose = () => {
    onCloseModal ? onCloseModal() : onClose();
  };

  const handleSave = () => {
    if (disabledSubmit) {
      return;
    }
    action.mutate(
      { containerAppId: containerId, appInstanceId, subDomain: customDomain },
      {
        onSuccess() {
          handleClose();
          setCustomDomain('');
          queryClient.invalidateQueries([
            'app-instance-detail-' + appInstanceId,
          ]);
        },
      },
    );
  };

  return (
    <>
      {typeof open === 'undefined' ? (
        <Button
          size='small'
          onClick={onShow}
          sx={{
            pl: 0,
            width: 'auto',
            minWidth: 100,
            textAlign: 'start',
            ml: '-4px !important',
          }}
        >
          Bind domain
        </Button>
      ) : (
        ''
      )}
      <Dialog open={show}>
        <DialogTitle sx={{ fontSize: 20 }}>Bind custom domain</DialogTitle>
        <DialogContent>
          <DialogContentText>
            By performing this action, you will be adding a custom domain name
            to your container app, allowing for a more personalized and
            professional URL for accessing your app. This will help enhance the
            branding and user experience.
          </DialogContentText>
          <Box sx={{ mt: 5 }}>
            <TextField
              label='Custom Domain Name'
              fullWidth
              value={customDomain ?? ''}
              placeholder='Enter text'
              error={!!unValidCustomDomain}
              helperText={unValidCustomDomain}
              InputProps={{
                startAdornment: (
                  <InputAdornment position='start'>
                    {prefix ? `${prefix}-` : ''}
                  </InputAdornment>
                ),
                endAdornment: (
                  <>
                    <InputAdornment position='end' sx={{ mr: 2 }}>
                      .joblogicapps.com
                    </InputAdornment>

                    {checkDomainAction.loading ? (
                      <CircularProgress size={16} />
                    ) : (
                      ''
                    )}
                    {!!customDomain && isChecking && checkDomainAction.data ? (
                      <CheckCircleIcon color='success' />
                    ) : (
                      ''
                    )}
                  </>
                ),
              }}
              onChange={(e) => {
                setCustomDomain(e.target.value);
                setIsChecking(false);
              }}
              onBlur={(e) => {
                if (!unValidCustomDomain && !isChecking) {
                  checkDomainAction.get({ subdomain: e.target.value }, () => {
                    setIsChecking(true);
                  });
                }
              }}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button disabled={action.isPending} onClick={handleClose}>
            Cancel
          </Button>
          <LoadingButton
            variant='contained'
            disabled={disabledSubmit}
            loading={action.isPending}
            onClick={handleSave}
          >
            Create
          </LoadingButton>
        </DialogActions>
      </Dialog>
    </>
  );
};

export default React.memo(BindDomainManualAction);

BindDomainManualAction.propTypes = {
  containerId: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  appInstanceId: PropTypes.string,
  open: PropTypes.bool,
  onCloseModal: PropTypes.func,
};
